/**
 * Centralized, bilingual toast helpers for Smart Canvas OS.
 *
 * Every helper emits a Sonner toast with a rich title + contextual description,
 * automatically localized via the current document language (set by I18nProvider).
 * Sonner is mounted bottom-left globally (see src/components/ui/sonner.tsx).
 */
import { toast } from "sonner";

type Lang = "en" | "es";
function lang(): Lang {
  if (typeof document !== "undefined") {
    const l = document.documentElement.lang;
    if (l === "es") return "es";
  }
  return "en";
}

type Msg = { title: string; description?: string };
type Bi = { en: Msg; es: Msg };

const pick = (m: Bi): Msg => m[lang()];

const ERR_FALLBACK: Bi = {
  en: { title: "Something went wrong", description: "Please try again in a moment." },
  es: { title: "Algo salió mal", description: "Por favor inténtalo de nuevo en un momento." },
};

function fromError(err: unknown, fallback: Bi = ERR_FALLBACK): Msg {
  const base = pick(fallback);
  const detail =
    typeof err === "string"
      ? err
      : (err as any)?.message ?? (err as any)?.error_description ?? null;
  return { title: base.title, description: detail || base.description };
}

/** Convenience renderers. */
function success(m: Bi) { const x = pick(m); toast.success(x.title, { description: x.description }); }
function error(m: Bi)   { const x = pick(m); toast.error(x.title, { description: x.description }); }
function info(m: Bi)    { const x = pick(m); toast(x.title, { description: x.description }); }

/** Public API — semantic OS toasts with rich, bilingual context. */
export const osToast = {
  /** Generic bilingual notifier (info-level). Use for cases not covered by semantic helpers. */
  notify: (m: Bi) => info(m),
  /** Generic bilingual success. Use when no semantic helper fits. */
  succeeded: (m: Bi) => success(m),
  /** Generic bilingual error. Use when no semantic helper fits. */
  errored: (m: Bi) => error(m),
  // Generic
  saved: () => success({
    en: { title: "Saved", description: "Your changes are now live." },
    es: { title: "Guardado", description: "Tus cambios están aplicados." },
  }),
  savedDraft: () => success({
    en: { title: "Draft saved", description: "You can keep editing later." },
    es: { title: "Borrador guardado", description: "Puedes seguir editando más tarde." },
  }),
  failed: (err?: unknown) => {
    const m = fromError(err, {
      en: { title: "Action failed", description: "We couldn't complete that action." },
      es: { title: "Acción fallida", description: "No pudimos completar esa acción." },
    });
    toast.error(m.title, { description: m.description });
  },
  failedToLoad: (err?: unknown, what?: string) => {
    const m = fromError(err, {
      en: { title: "Couldn't load data", description: what ? `We couldn't load ${what}.` : "Please refresh the page." },
      es: { title: "No se pudo cargar", description: what ? `No pudimos cargar ${what}.` : "Por favor recarga la página." },
    });
    toast.error(m.title, { description: m.description });
  },

  // Entities — created / updated / deleted
  created: (entityEn: string, entityEs: string, name?: string, isFeminine?: boolean) => success({
    en: { title: `${capitalize(entityEn)} created`, description: name ? `"${name}" is now in your workspace.` : `New ${entityEn} added to your workspace.` },
    es: { title: `${capitalize(entityEs)} ${isFeminine ? 'creada' : 'creado'}`, description: name ? `"${name}" ya está en tu espacio.` : `${isFeminine ? 'Nueva' : 'Nuevo'} ${entityEs} ${isFeminine ? 'agregada' : 'agregado'} a tu espacio.` },
  }),
  updated: (entityEn: string, entityEs: string, name?: string, isFeminine?: boolean) => success({
    en: { title: `${capitalize(entityEn)} updated`, description: name ? `Changes to "${name}" are saved.` : `Your changes were saved.` },
    es: { title: `${capitalize(entityEs)} ${isFeminine ? 'actualizada' : 'actualizado'}`, description: name ? `Los cambios en "${name}" se guardaron.` : `Tus cambios se guardaron.` },
  }),
  deleted: (entityEn: string, entityEs: string, name?: string, isFeminine?: boolean) => success({
    en: { title: `${capitalize(entityEn)} deleted`, description: name ? `"${name}" was removed.` : `Item removed from your workspace.` },
    es: { title: `${capitalize(entityEs)} ${isFeminine ? 'eliminada' : 'eliminado'}`, description: name ? `"${name}" fue ${isFeminine ? 'eliminada' : 'eliminado'}.` : `Elemento eliminado de tu espacio.` },
  }),

  // Validation
  required: (fieldEn: string, fieldEs: string) => error({
    en: { title: "Missing required field", description: `Please enter a ${fieldEn} before continuing.` },
    es: { title: "Falta un campo obligatorio", description: `Por favor ingresa un ${fieldEs} antes de continuar.` },
  }),
  invalidImage: () => error({
    en: { title: "Invalid image", description: "Only PNG, JPG or WebP images are supported." },
    es: { title: "Imagen no válida", description: "Solo se aceptan imágenes PNG, JPG o WebP." },
  }),
  imageTooLarge: (maxMb = 4) => error({
    en: { title: "Image is too large", description: `Please use an image under ${maxMb} MB.` },
    es: { title: "Imagen demasiado grande", description: `Usa una imagen menor a ${maxMb} MB.` },
  }),

  // Project domain
  milestoneAdded: (title?: string) => success({
    en: { title: "Milestone added", description: title ? `"${title}" is now tracked on this project.` : "It is now tracked on this project." },
    es: { title: "Hito agregado", description: title ? `"${title}" ya se rastrea en este proyecto.` : "Ya se rastrea en este proyecto." },
  }),
  taskAdded: (title?: string) => success({
    en: { title: "Task added", description: title ? `"${title}" was added to the task list.` : "It was added to the task list." },
    es: { title: "Tarea agregada", description: title ? `"${title}" se agregó a la lista de tareas.` : "Se agregó a la lista de tareas." },
  }),
  updatePosted: () => success({
    en: { title: "Update posted", description: "Your team and client can see this update now." },
    es: { title: "Actualización publicada", description: "Tu equipo y cliente pueden verla ahora." },
  }),
  progressSaved: (pct: number, status: string) => success({
    en: { title: "Project progress saved", description: `Now at ${pct}% · status: ${status}.` },
    es: { title: "Progreso guardado", description: `Ahora en ${pct}% · estado: ${status}.` },
  }),

  // Team / project team
  teamAssigned: (name?: string) => success({
    en: { title: "Added to project team", description: name ? `${name} can now collaborate on this project.` : "They can now collaborate on this project." },
    es: { title: "Agregado al equipo del proyecto", description: name ? `${name} ya puede colaborar en este proyecto.` : "Ya puede colaborar en este proyecto." },
  }),
  teamRemoved: (name?: string) => success({
    en: { title: "Removed from project team", description: name ? `${name} no longer has this project assigned.` : "They no longer have this project assigned." },
    es: { title: "Removido del equipo del proyecto", description: name ? `${name} ya no tiene este proyecto asignado.` : "Ya no tiene este proyecto asignado." },
  }),

  // Roles
  roleAdded: (role?: string, name?: string) => success({
    en: { title: "Role granted", description: role && name ? `${name} is now a ${role}.` : "Permissions updated." },
    es: { title: "Rol otorgado", description: role && name ? `${name} ahora es ${role}.` : "Permisos actualizados." },
  }),
  roleRemoved: (role?: string, name?: string) => success({
    en: { title: "Role revoked", description: role && name ? `${name} is no longer a ${role}.` : "Permissions updated." },
    es: { title: "Rol removido", description: role && name ? `${name} ya no es ${role}.` : "Permisos actualizados." },
  }),

  // Account
  profileUpdated: () => success({
    en: { title: "Profile updated", description: "Your changes are visible across the OS." },
    es: { title: "Perfil actualizado", description: "Tus cambios son visibles en todo el OS." },
  }),
  avatarUpdated: () => success({
    en: { title: "Profile photo updated", description: "Your new picture is live." },
    es: { title: "Foto de perfil actualizada", description: "Tu nueva foto ya está activa." },
  }),
  avatarRemoved: () => success({
    en: { title: "Profile photo removed", description: "We'll show your initials instead." },
    es: { title: "Foto de perfil eliminada", description: "Mostraremos tus iniciales en su lugar." },
  }),

  // Info
  comingSoon: () => info({
    en: { title: "Coming soon", description: "We're still polishing this — check back shortly." },
    es: { title: "Próximamente", description: "Aún lo estamos puliendo — vuelve pronto." },
  }),
};

function capitalize(s: string) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

/** Re-export raw toast for one-off rich messages. */
export { toast };
